<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'

import type {
  ComponentNode,
  OfferConst,
  OfferGroupDataDto,
  PlanSelectionOption,
} from '@api/types.gen'

import TipTap from '@render/lib/TipTap'

import { PipeDisplayMode, usePipe } from '@render/composables/usePipe'
import { readOfferGroupForOption, readPriceCategory } from '@render/lib/planOption'
import { evalRule } from '@render/composables/useLogic'
import { useLocale } from '@render/composables/useLocale'
import { emitPayment, PAYMENT_EVENTS } from '@render/composables/usePaymentEvents'
import { useTranslation } from '@render/composables/useTranslation'
import type { ColorField, SizeField } from '@render/types/styles'
import {
  composeShadow,
  getColorStyleData,
  getSizeStyleData,
  getSpacingStyleData,
} from '@render/composables/useStyleData'

import OfferSelectV2Card from './OfferSelectV2Card.vue'
import type { OfferCardCss, OfferCardData } from './cardStyles'
import type { OfferSelectV2Fields as Model } from './definition'
import type { ConditionalBadge } from '../OfferSelectComponent/types'

const pipe = usePipe()
const fields = defineModel<Model>('fields', {
  required: true,
  default: () => ({
    plans: { type: 'plan_selection_options', options: [] },
    valueVariable: undefined,
    selectState: 'idle',
  }),
})

defineProps<{
  slotChildren?: Record<string, ComponentNode[]>
  editable?: boolean
}>()

const emit = defineEmits<{ (e: 'onClick'): void }>()

const isEditor = computed(() => pipe.currentMode.value === PipeDisplayMode.EDITOR)

// ── Exit-intent mode ─────────────────────────────────────────────────────────
// One boolean drives both the pricing (which offer variant is charged) and the
// styling (which colour overrides apply). The author can force it on in the
// editor to work on the post-exit-intent design without firing a real exit
// intent — the flag is EDITOR-gated so it can never leak into a built pipe.
const isExitIntent = computed(
  () =>
    !!pipe.getData('var.global.hasExitIntentBeenAccepted') ||
    (isEditor.value && fields.value.previewExitIntent === true),
)

function color(f: ColorField | undefined, fallback = 'transparent') {
  return getColorStyleData(f) || fallback
}
function size(f: SizeField | undefined, fallback = '0px') {
  return getSizeStyleData(f) || fallback
}

// An exit-intent colour override falls back to its non-exit twin, which in turn
// falls back to the theme default — so the red design is authored by setting
// only the tokens that actually differ.
function exitColor(
  exitField: ColorField | undefined,
  baseField: ColorField | undefined,
  fallback: string,
) {
  if (isExitIntent.value) return color(exitField, '') || color(baseField, fallback)
  return color(baseField, fallback)
}

const css = computed<OfferCardCss>(() => ({
  // Option card
  paddingY: size(fields.value.paddingY, '16px'),
  paddingX: size(fields.value.paddingX, '12px'),
  gapColumns: size(fields.value.gapColumns, '4px'),
  gapPriceRow: size(fields.value.gapPriceRow, '6px'),
  gapNoteRow: size(fields.value.gapNoteRow, '6px'),
  borderRadius: size(fields.value.borderRadius, 'var(--radius-xs)'),
  borderWidth: size(fields.value.borderWidth, '1px'),
  borderWidthPressed: size(fields.value.borderWidthPressed, size(fields.value.borderWidth, '1px')),
  bgIdle: exitColor(
    fields.value.exitBgColor,
    fields.value.bgColor,
    'var(--color-background-surface-raised)',
  ),
  bgHover: color(fields.value.bgColorHover, 'var(--color-background-surface)'),
  bgPressed: exitColor(
    fields.value.exitBgColorPressed,
    fields.value.bgColorPressed,
    'var(--color-background-surface)',
  ),
  borderIdle: exitColor(
    fields.value.exitBorderColor,
    fields.value.borderColor,
    'var(--color-input-border)',
  ),
  borderPressed: exitColor(
    fields.value.exitBorderColorPressed,
    fields.value.borderColorPressed,
    'var(--color-input-border-active)',
  ),
  shadowIdle: composeShadow(
    fields.value.dropShadow,
    fields.value.innerShadow,
    'var(--shadow-selectors-default)',
  ),
  shadowPressed: composeShadow(
    fields.value.pressedDropShadow,
    fields.value.pressedInnerShadow,
    'var(--shadow-selectors-default-pressed)',
  ),

  // Radio container
  bgRadioContainer: exitColor(
    fields.value.exitBgColorRadioContainer,
    fields.value.bgColorRadioContainer,
    'var(--color-action-secondary-background)',
  ),
  bgRadioContainerPressed: exitColor(
    fields.value.exitBgColorRadioContainerPressed,
    fields.value.bgColorRadioContainerPressed,
    'var(--color-action-secondary-background-pressed)',
  ),
  bgRadioFill: exitColor(
    fields.value.exitBgColorRadioFill,
    fields.value.bgColorRadioFill,
    'var(--color-brand-primary)',
  ),

  // Highlight header
  bgHighlightHeader: color(
    fields.value.bgColorHighlightHeader,
    'var(--color-brand-primary-subtle)',
  ),
  bgHighlightHeaderPressed: color(
    fields.value.bgColorHighlightHeaderPressed,
    'var(--color-brand-primary-subtle)',
  ),

  // Text sizes
  textSizeHighlightLabel: size(fields.value.textSizeHighlightLabel, 'var(--text-body-sm)'),
  textSizeTitle: size(fields.value.textSizeTitle, 'var(--text-body-lg-size)'),
  textSizeOldPrice: size(fields.value.textSizeOldPrice, 'var(--text-body-sm)'),
  textSizePreviousPrice: size(
    fields.value.textSizePreviousPrice,
    size(fields.value.textSizeOldPrice, 'var(--text-body-sm)'),
  ),

  // Text colours
  textColorHighlightLabel: color(
    fields.value.textColorHighlightLabel,
    'var(--color-action-secondary-text)',
  ),
  textColorHighlightLabelPressed: color(
    fields.value.textColorHighlightLabelPressed,
    'var(--color-action-secondary-text)',
  ),
  textColorTitle: color(fields.value.textColorTitle, 'var(--color-text-secondary)'),
  textColorTitlePressed: color(fields.value.textColorTitlePressed, 'var(--color-text-primary)'),
  textColorOldPrice: color(fields.value.textColorOldPrice, 'var(--color-text-tertiary)'),
  textColorOldPricePressed: color(
    fields.value.textColorOldPricePressed,
    'var(--color-text-tertiary)',
  ),
  textColorPreviousPrice: exitColor(
    fields.value.exitTextColorPreviousPrice,
    fields.value.textColorPreviousPrice,
    'var(--color-text-tertiary)',
  ),
  textColorPreviousPricePressed: exitColor(
    fields.value.exitTextColorPreviousPrice,
    fields.value.textColorPreviousPricePressed,
    'var(--color-text-tertiary)',
  ),

  // `currentColor` outside exit intent, so the line keeps matching its own text
  // and an untouched card renders byte-identical CSS.
  strikethroughColor: isExitIntent.value
    ? color(fields.value.exitStrikethroughColor, 'currentColor')
    : 'currentColor',

  // Badge
  badgeBg: color(fields.value.badgeBgColor, 'var(--color-background-subtle)'),
  badgeTextColor: color(fields.value.badgeTextColor, 'var(--color-text-accent)'),
  badgeFontSize: size(fields.value.badgeFontSize, 'var(--text-label-sm-size)'),
  badgeFontWeight: fields.value.badgeFontWeight || 'var(--font-weight-medium)',
  badgeBorderRadius: size(fields.value.badgeBorderRadius, 'var(--radius-xs)'),
  badgeBorderWidth: size(fields.value.badgeBorderWidth, '0'),
  badgeBorderColor: color(fields.value.badgeBorderColor, 'transparent'),
  badgePadding: getSpacingStyleData(fields.value.badgePadding) || 'var(--space-2) var(--space-8)',
  badgeMargin: getSpacingStyleData(fields.value.badgeMargin) || '0',
  badgeMinHeight: size(fields.value.badgeMinHeight, '20px'),
}))

const containerCss = computed(() => ({
  gapRows: size(fields.value.gapRows, '16px'),
  marginTop: size(fields.value.marginTop, '16px'),
  marginBottom: size(fields.value.marginBottom, '16px'),
  maxWidth: size(fields.value.maxWidth, '480px'),
  minHeight: size(fields.value.minHeight, '92px'),
}))

// Prevent layout micro-shifts during selection when the badge is in play, as its
// height is larger than the row it sits in. A plan with its own badge override
// reserves the space regardless of the global dropdown.
const isBadgeTurnedOn = (option: PlanSelectionOption): boolean =>
  option.badgeVisibility === 'on' ||
  (!!fields.value.badgeVisibility && fields.value.badgeVisibility !== 'off')

const previewClass = computed(() => `is-${fields.value.selectState || 'idle'}`)

const showBestDealHeader = computed(() =>
  isExitIntent.value ? fields.value.exitShowBestDealHeader === true : true,
)
const showPreviousPrice = computed(() => fields.value.exitShowPreviousPrice !== false)

// ── Offer resolution ─────────────────────────────────────────────────────────

const getOfferData = (offerData: OfferGroupDataDto): OfferConst => {
  if (isExitIntent.value && offerData.exit) return offerData.exit

  return offerData.regular
}

const priceCategory = computed(() =>
  readPriceCategory((path) => pipe.getData(path), fields.value.priceCategory),
)

const getOfferDataForOption = (option: PlanSelectionOption): OfferGroupDataDto | undefined =>
  readOfferGroupForOption((path) => pipe.getData(path), option, priceCategory.value)

const isTrialPrice = (offer: OfferConst): boolean => offer.paymentType === 'recurring_with_trial'

const selectedOfferData = computed<OfferGroupDataDto>({
  get() {
    return (pipe.getData(`var.${fields.value.valueVariable?.key ?? ''}`) ?? []) as OfferGroupDataDto
  },
  set(val) {
    pipe.setData(`var.${fields.value.valueVariable?.key ?? ''}`, val)
  },
})

const isActiveOffer = (option: PlanSelectionOption): boolean => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return false

  const selectedOffer = selectedOfferData.value
  if (!selectedOffer || Array.isArray(selectedOffer)) return false

  const selectedVariant = getOfferData(selectedOffer)
  const optionVariant = getOfferData(offerData)
  return (
    selectedVariant?.apiBasePrice === optionVariant?.apiBasePrice &&
    selectedVariant?.ppoId === optionVariant?.ppoId
  )
}

const isBestDealOffer = (option: PlanSelectionOption): boolean => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) return false

  const variant = getOfferData(offerData)
  const periodPrice = isTrialPrice(variant)
    ? variant.trialPricePerPeriod
    : variant.basePricePerPeriod
  if (!periodPrice) return false

  // Every option's per-period price, to decide whether this one is the cheapest.
  const allPeriodPrices = (fields.value.plans?.options ?? [])
    .map((opt) => {
      const data = getOfferDataForOption(opt)
      if (!data) return null

      const variantForOpt = getOfferData(data)
      return isTrialPrice(variantForOpt)
        ? variantForOpt.trialPricePerPeriod
        : variantForOpt.basePricePerPeriod
    })
    .filter((price): price is number => price !== undefined && price !== null)

  return allPeriodPrices.every((price) => periodPrice <= price)
}

const isDefaultOption = (option: PlanSelectionOption): boolean =>
  !!fields.value.plans?.defaultOptionId && option.id === fields.value.plans.defaultOptionId

const locale = useLocale()
const formatPrice = (value: number | string | undefined | null) => {
  if (value === undefined || value === null) return ''
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (!Number.isFinite(num)) return ''
  return locale.formatCurrencyShortTrim(num)
}

// Everything a card needs to display, resolved from the offer constants. The
// exit variant supplies `price`; `previousPrice` is the pre-exit-intent price of
// the same plan, which is what the post-exit design strikes through beside the
// original — it is read off `regular` regardless of which variant is charged.
const cardData = (option: PlanSelectionOption): OfferCardData => {
  const offerData = getOfferDataForOption(option)
  if (!offerData) {
    return {
      price: '',
      oldPrice: '',
      previousPrice: '',
      periodPrice: '',
      savedAmount: '',
      discountPercent: 0,
      periodCount: 0,
      periodUnit: '',
    }
  }

  const variant = getOfferData(offerData)
  const trial = isTrialPrice(variant)

  const charged = trial ? variant.apiTrialPrice : variant.apiBasePrice
  const woDiscount = trial ? variant.trialPriceWoDiscount : variant.basePriceWoDiscount
  const perPeriod = trial ? variant.trialPricePerPeriod : variant.basePricePerPeriod
  const discount = trial ? variant.trialPriceDiscountPercent : variant.basePriceDiscountPercent

  const regular = offerData.regular
  const regularTrial = isTrialPrice(regular)
  const previous = regularTrial ? regular.apiTrialPrice : regular.apiBasePrice

  const saved =
    woDiscount !== undefined && woDiscount !== null && charged !== undefined && charged !== null
      ? woDiscount - charged
      : undefined

  return {
    price: formatPrice(charged),
    oldPrice: formatPrice(woDiscount),
    previousPrice: isExitIntent.value ? formatPrice(previous) : '',
    periodPrice: formatPrice(perPeriod),
    savedAmount: formatPrice(saved),
    discountPercent: Math.round(discount ?? 0),
    periodCount: (trial ? variant.trialPeriod : variant.basePeriod) ?? 0,
    periodUnit: (trial ? variant.trialPeriodUnit : variant.basePeriodUnit) ?? '',
  }
}

// ── Selection ────────────────────────────────────────────────────────────────

const clickOption = (option: PlanSelectionOption) => {
  const offerData = getOfferDataForOption(option)

  if (!offerData) {
    console.warn(
      `No offer group data for plan option ${option.id} (category key ${option.categoryKey ?? 'none'}, constant ${option.offerGroup?.key ?? 'none'}). Cannot set current offer group.`,
    )
    return
  }

  selectedOfferData.value = offerData
  pipe.setData('var.global.selectedOfferGroupIsDefault', isDefaultOption(option))
  emit('onClick')
}

// True when the stored selection corresponds to one of THIS selector's options.
// Drives "seed the default only when nothing valid is selected": a user pick is
// never clobbered, while a cross-pipe restored offer falls back to the default.
const hasValidSelection = computed(() => (fields.value.plans?.options ?? []).some(isActiveOffer))

function applyDefaultOption(defaultId: string | undefined) {
  if (!defaultId) return
  // Never overwrite a selection that already matches one of our options.
  if (hasValidSelection.value) return

  const defaultOption = fields.value.plans?.options.find((o) => o.id === defaultId)
  if (!defaultOption) return

  const offerData = getOfferDataForOption(defaultOption)
  if (!offerData) return

  selectedOfferData.value = offerData
  pipe.setData('var.global.selectedOfferGroupIsDefault', true)
  // Mirrors legacy `initDefaultSelectedPlan` — fires `plan selected` for the
  // default-selected offer too, not only on user click.
  emitPayment({ pipe, event: PAYMENT_EVENTS.planSelected, offer: offerData })
  // Smaller payload for Klaviyo/Segment, matching CPPv3.
  emitPayment({ pipe, event: PAYMENT_EVENTS.trackingPlanSelectedSlimPayload, offer: offerData })
}

onMounted(async () => {
  // The persisted-vars merge runs inside a setTimeout(0); wait for it so we read
  // the real selection (and any AC-restored offer) before deciding to seed.
  await pipe.whenReady()
  applyDefaultOption(fields.value.plans?.defaultOptionId)
})

// Re-evaluate when the stored offer changes (e.g. an AC restore lands after
// mount and writes a non-matching offer) or the configured default changes.
watch(
  () => [selectedOfferData.value, fields.value.plans?.defaultOptionId],
  () => applyDefaultOption(fields.value.plans?.defaultOptionId),
)

// ── Badges ───────────────────────────────────────────────────────────────────

const bestDealValue = useTranslation(() => fields.value.bestDealTranslation?.key)

const conditionalBadges = computed(() => fields.value.conditionalBadges?.items ?? [])

// Collect every `{ field: '...' }` key referenced in a json-logic rule tree so we
// know which pipe-data paths to re-evaluate on. Mirrors NutritionistChat.
function collectRuleFieldKeys(rule: unknown, out: Set<string>) {
  if (!rule || typeof rule !== 'object') return
  const node = rule as Record<string, unknown>
  if ('field' in node) {
    const arg = Array.isArray(node.field) ? (node.field as unknown[])[0] : node.field
    if (typeof arg === 'string') out.add(arg)
  }
  for (const child of Object.values(node)) {
    if (Array.isArray(child)) child.forEach((c) => collectRuleFieldKeys(c, out))
    else if (child && typeof child === 'object') collectRuleFieldKeys(child, out)
  }
}

// Plan options that opted into their own badge label list, keyed by option id —
// these override the shared conditionalBadges list below for that option only.
const optionOverrides = computed(() =>
  (fields.value.plans?.options ?? []).filter((o) => o.badgeVisibility === 'on'),
)

const ruleDependencyKeys = computed(() => {
  const keys = new Set<string>()
  for (const item of conditionalBadges.value) collectRuleFieldKeys(item.rules, keys)
  for (const option of optionOverrides.value) {
    for (const item of option.conditionalBadges?.items ?? []) collectRuleFieldKeys(item.rules, keys)
  }
  return Array.from(keys)
})

// Resolve a badge list (shared or a plan option's own) to the translation key of
// its first matching item, or undefined when none match. `evalRule` is
// fail-open, so an item with no rule always matches — that's how an
// unconditional badge is expressed.
async function resolveActiveBadgeKey(items: ConditionalBadge[]): Promise<string | undefined> {
  // In the editor, rules are evaluated so the canvas reflects the current
  // conditions — unless the author turned visibility rules off ("show all"),
  // in which case fall back to the first authored text so styling stays
  // visible. Live builds always evaluate.
  if (isEditor.value && pipe.disableAllVisibilityRules.value) {
    return items.find((i) => i.text?.key)?.text?.key
  }
  for (const item of items) {
    if (!item.text?.key) continue
    if (await evalRule(item.rules, (k) => pipe.getData(k))) return item.text.key
  }
  return undefined
}

// Translation key of the active shared badge, re-evaluated whenever the list or
// any referenced pipe-data value changes.
const activeBadgeKey = ref<string | undefined>(undefined)
// Same, but per plan option that opted into its own list (keyed by option id).
const optionActiveBadgeKeys = ref<Record<string, string | undefined>>({})

watch(
  [
    isEditor,
    () => pipe.disableAllVisibilityRules.value,
    conditionalBadges,
    optionOverrides,
    () => ruleDependencyKeys.value.map((k) => pipe.getData(k)),
  ],
  async () => {
    activeBadgeKey.value = await resolveActiveBadgeKey(conditionalBadges.value)

    const next: Record<string, string | undefined> = {}
    for (const option of optionOverrides.value) {
      next[option.id] = await resolveActiveBadgeKey(option.conditionalBadges?.items ?? [])
    }
    optionActiveBadgeKeys.value = next
  },
  { immediate: true, deep: true },
)

// A plan option with its own badge list (badgeVisibility: 'on') overrides the
// shared badge text — for that option only.
const badgeKeyForOption = (option: PlanSelectionOption): string | undefined =>
  option.badgeVisibility === 'on' ? optionActiveBadgeKeys.value[option.id] : activeBadgeKey.value

function setBadgeTranslation(option: PlanSelectionOption, value: object) {
  const key = badgeKeyForOption(option)
  if (key) pipe.setTranslation(key, value)
}

const shouldShowBadge = (option: PlanSelectionOption): boolean => {
  if (!badgeKeyForOption(option)) return false

  // A plan with its own badge override shows it unconditionally — the override
  // replaces the global "which cards show a badge" gate for that option, not
  // just its text. The global dropdown still gates every option with no
  // override of its own.
  if (option.badgeVisibility === 'on') return true

  switch (fields.value.badgeVisibility) {
    case 'all':
      return true
    case 'selected':
      return isActiveOffer(option)
    case 'best-deal':
      return isBestDealOffer(option)
    default:
      return false
  }
}
</script>

<template>
  <div class="offer2-container" role="radiogroup">
    <OfferSelectV2Card
      v-for="option in fields.plans?.options ?? []"
      :key="option.id"
      :option="option"
      :data="cardData(option)"
      :css="css"
      :is-active="isActiveOffer(option)"
      :is-best-deal="isBestDealOffer(option)"
      :is-exit-intent="isExitIntent"
      :show-best-deal-header="showBestDealHeader"
      :show-previous-price="showPreviousPrice"
      :badge-turned-on="isBadgeTurnedOn(option)"
      :show-badge="shouldShowBadge(option)"
      :preview-class="previewClass"
      :slot-children="slotChildren"
      :editable="editable"
      @select="clickOption(option)"
    >
      <template #best-deal>
        <TipTap v-model="bestDealValue" mode="single" />
      </template>
      <template #badge>
        <TipTap
          :model-value="pipe.getTranslation(badgeKeyForOption(option))"
          @update:model-value="(val) => setBadgeTranslation(option, val as object)"
          mode="single"
        />
      </template>
    </OfferSelectV2Card>
  </div>
</template>

<style scoped lang="scss">
.offer2-container {
  display: flex;
  width: 100%;
  max-width: v-bind('containerCss.maxWidth');
  min-height: v-bind('containerCss.minHeight');
  flex-direction: column;
  gap: v-bind('containerCss.gapRows');
  margin-top: v-bind('containerCss.marginTop');
  margin-bottom: v-bind('containerCss.marginBottom');
}
</style>
